import React, { useState, useEffect, useMemo } from 'react';
import TabContent from '../../../components/TabContent';
import Input from '../../../components/input';
import Button from '../../../components/button';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  createLoadBalancer,
  getSecrets,
  getUserVirtualAccount,
} from '../../../hooks/local/userReducer';
import { useNetworkList } from '../layout/resuableEffect';
import LoadBalancerInstances from './loadBalancerInstances';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaymentModal from '../../../components/paymentModal';
import useZoneUnitPrices from "../../../hooks/useZoneUnitPrices";

const CreateLoadBalancer = () => {
  document.title = 'Create Load Balancer - Cloudvantage';

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedProject = useSelector(
    (state) => state.user.selectedProject,
  );

  const projectId = selectedProject?.id;

  const selectedZoneId =
    selectedProject?.zone_id ||
    selectedProject?.zone?.id ||
    selectedProject?.zone?.zone_id ||
    selectedProject?.zone_details?.id ||
    selectedProject?.zone_details?.zone_id ||
    '';

  const { getUnitPrice, loading: unitPriceLoading } =
    useZoneUnitPrices(projectId);

  const currency = useSelector((state) => state.user.currency);

  const poolMemberUnitPrice = getUnitPrice('pool_member', currency);

  const calculatePoolMemberCost = (values) => {
    if (poolMemberUnitPrice === null) {
      return 0;
    }

    if (values?.createPool !== 'yes') {
      return 0;
    }

    const memberCount = values?.pools?.[0]?.members?.length || 0;

    return memberCount * poolMemberUnitPrice;
  };

  const virtualAccount = useSelector(
    (state) => state.user.virtualAccount,
  );

  const [walletLoading, setWalletLoading] = useState(true);

  const getWalletBalance = (account) => {
    if (!account) return null;

    const rawBalance =
      currency === 'USD'
        ? account?.dollar_balance
        : account?.balance;

    if (
      rawBalance === undefined ||
      rawBalance === null ||
      rawBalance === ''
    ) {
      return null;
    }

    const numericBalance = Number(rawBalance);

    return Number.isNaN(numericBalance) ? null : numericBalance;
  };

  const walletBalance = getWalletBalance(virtualAccount);

  // Refresh the wallet whenever the user enters this page or switches
  // project/region. A missing wallet must never be treated as a zero balance.
  useEffect(() => {
    let isMounted = true;

    const refreshWallet = async () => {
      try {
        if (isMounted) {
          setWalletLoading(true);
        }

        await dispatch(getUserVirtualAccount());
      } catch (error) {
        console.error('Unable to refresh wallet:', error);
      } finally {
        if (isMounted) {
          setWalletLoading(false);
        }
      }
    };

    refreshWallet();

    return () => {
      isMounted = false;
    };
  }, [dispatch, projectId, selectedZoneId]);

  const networkList = useNetworkList(projectId);
  const [isLoadingNetworks, setIsLoadingNetworks] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    const fetchCertificates = async () => {
      if (projectId) {
        const result = await dispatch(getSecrets(projectId));
        if (result?.payload) {
          setCertificates(result?.payload);
        }
      }
    };
    fetchCertificates();
  }, [projectId, dispatch]);

  const networkListOptions = useMemo(() => {
    const baseOptions = [
      { label: 'Select network', value: '' },
      { label: 'External', value: '5236d605-3714-41f8-b1c1-6247d3f15cdb' },
    ];


    const externalNetwork = networkList.find(
      (network) => network.id === '5236d605-3714-41f8-b1c1-6247d3f15cdb',
    );
    if (externalNetwork) {
      baseOptions.push({
        label: 'External',
        value: '5236d605-3714-41f8-b1c1-6247d3f15cdb',
      });
    }

    if (!Array.isArray(networkList) || networkList.length === 0) {
      return isLoadingNetworks
        ? [{ label: 'Loading networks...', value: '' }]
        : baseOptions;
    }

    return [
      ...baseOptions,
      ...networkList.map((network) => ({
        label: network?.name || 'Unnamed Network',
        value: network?.id || '',
      })),
    ];
  }, [networkList, isLoadingNetworks]);

  useEffect(() => {
    if (networkList !== null && networkList !== undefined) {
      setIsLoadingNetworks(false);
    }
  }, [networkList]);

  const [selectedTab, setSelectedTab] = useState('Load Balancer Details *');
  const [tabPage, setTabPage] = useState(1);
  const entriesTabPerPage = 5;
  const [isTabValid, setIsTabValid] = useState({
    'Load Balancer Details *': false,
    'Listener Details *': false,
    'Pool Details *': false,
    'Pool Members': false,
    'Monitor Details *': false,
  });

  const tabsBalancers = [
    'Load Balancer Details *',
    'Listener Details *',
    'Pool Details *',
    'Pool Members',
    'Monitor Details *',
  ];

  const getErrorDetails = (errors, touched) => {
    const errorDetails = [];
    const addError = (tab, field, message) => {
      errorDetails.push({ tab, field, message });
    };

    if (touched.name && errors.name)
      addError('Load Balancer Details *', 'Name', errors.name);
    if (touched.vip_network_id && errors.vip_network_id)
      addError('Load Balancer Details *', 'Network', errors.vip_network_id);
    if (touched.createListener && errors.createListener)
      addError('Listener Details *', 'Create Listener', errors.createListener);
    if (errors.listeners?.[0]) {
      const listenerErrors = errors.listeners[0];
      if (touched.listeners?.[0]?.name && listenerErrors.name)
        addError('Listener Details *', 'Listener Name', listenerErrors.name);
      if (touched.listeners?.[0]?.protocol && listenerErrors.protocol)
        addError('Listener Details *', 'Protocol', listenerErrors.protocol);
      if (touched.listeners?.[0]?.protocol_port && listenerErrors.protocol_port)
        addError('Listener Details *', 'Port', listenerErrors.protocol_port);
      if (
        touched.listeners?.[0]?.default_tls_container_ref &&
        listenerErrors.default_tls_container_ref
      )
        addError(
          'Monitor Details *',
          'SSL Certificate',
          listenerErrors.default_tls_container_ref,
        );
    }
    if (touched.createPool && errors.createPool)
      addError('Pool Details *', 'Create Pool', errors.createPool);
    if (errors.pools?.[0]) {
      const poolErrors = errors.pools[0];
      if (touched.pools?.[0]?.name && poolErrors.name)
        addError('Pool Details *', 'Pool Name', poolErrors.name);
      if (
        touched.pools?.[0]?.session_persistence?.type &&
        poolErrors.session_persistence?.type
      )
        addError(
          'Pool Details *',
          'Session Persistence',
          poolErrors.session_persistence.type,
        );
      if (
        touched.pools?.[0]?.session_persistence?.cookie_name &&
        poolErrors.session_persistence?.cookie_name
      )
        addError(
          'Pool Details *',
          'Cookie Name',
          poolErrors.session_persistence.cookie_name,
        );
      if (touched.pools?.[0]?.lb_algorithm && poolErrors.lb_algorithm)
        addError('Pool Details *', 'Algorithm', poolErrors.lb_algorithm);
    }
    if (errors.pools?.[0]?.members) {
      errors.pools[0].members.forEach((memberError, index) => {
        if (
          touched.pools?.[0]?.members?.[index]?.address &&
          memberError?.address
        )
          addError(
            'Pool Members',
            `Member ${index + 1} IP Address`,
            memberError.address,
          );
        if (
          touched.pools?.[0]?.members?.[index]?.protocol_port &&
          memberError?.protocol_port
        )
          addError(
            'Pool Members',
            `Member ${index + 1} Port`,
            memberError.protocol_port,
          );
        if (touched.pools?.[0]?.members?.[index]?.weight && memberError?.weight)
          addError(
            'Pool Members',
            `Member ${index + 1} Weight`,
            memberError.weight,
          );
      });
    }
    if (
      touched.pools?.[0]?.createHealthMonitor &&
      errors.pools?.[0]?.createHealthMonitor
    )
      addError(
        'Monitor Details *',
        'Create Health Monitor',
        errors.pools[0].createHealthMonitor,
      );
    if (errors.pools?.[0]?.healthmonitor) {
      const monitorErrors = errors.pools[0].healthmonitor;
      if (touched.pools?.[0]?.healthmonitor?.name && monitorErrors.name)
        addError('Monitor Details *', 'Monitor Name', monitorErrors.name);
      if (touched.pools?.[0]?.healthmonitor?.type && monitorErrors.type)
        addError('Monitor Details *', 'Monitor Type', monitorErrors.type);
      if (touched.pools?.[0]?.healthmonitor?.delay && monitorErrors.delay)
        addError('Monitor Details *', 'Delay', monitorErrors.delay);
      if (
        touched.pools?.[0]?.healthmonitor?.max_retries &&
        monitorErrors.max_retries
      )
        addError('Monitor Details *', 'Max Retries', monitorErrors.max_retries);
      if (touched.pools?.[0]?.healthmonitor?.timeout && monitorErrors.timeout)
        addError('Monitor Details *', 'Timeout', monitorErrors.timeout);
      if (
        touched.pools?.[0]?.healthmonitor?.http_method &&
        monitorErrors.http_method
      )
        addError('Monitor Details *', 'HTTP Method', monitorErrors.http_method);
      if (
        touched.pools?.[0]?.healthmonitor?.expected_codes &&
        monitorErrors.expected_codes
      )
        addError(
          'Monitor Details *',
          'Expected Codes',
          monitorErrors.expected_codes,
        );
      if (touched.pools?.[0]?.healthmonitor?.url_path && monitorErrors.url_path)
        addError('Monitor Details *', 'URL Path', monitorErrors.url_path);
      if (
        touched.pools?.[0]?.healthmonitor?.http_version &&
        monitorErrors.http_version
      )
        addError(
          'Monitor Details *',
          'HTTP Version',
          monitorErrors.http_version,
        );
    }

    return errorDetails;
  };

  const createLoadBalanceForm = useFormik({
    initialValues: {
      name: '',
      vip_address: '',
      vip_network_id: '',
      createListener: 'yes',
      listeners: [
        {
          name: '',
          description: '',
          protocol: '',
          protocol_port: '',
          timeout_client_data: 50000,
          timeout_tcp_inspect: 0,
          timeout_member_connect: 5000,
          timeout_member_data: 50000,
          connection_limit: -1,
          alpn_protocols: ['http/1.0', 'http/1.1', 'h2'],
          allowed_cidrs: '',
          default_tls_container_ref: '',
        },
      ],
      createPool: 'yes',
      pools: [
        {
          name: '',
          alpn_protocols: ['http/1.0', 'http/1.1', 'h2'],
          protocol: '',
          session_persistence: {
            type: '',
            cookie_name: '',
            persistence_timeout: '',
            persistence_granularity: '',
          },
          lb_algorithm: '',
          members: [
            {
              address: '',
              protocol_port: '',
              name: '',
              weight: 1,
              backup: false,
              monitor_address: '',
              monitor_port: '',
            },
          ],
          createHealthMonitor: 'yes',
          healthmonitor: {
            type: '',
            delay: 5,
            max_retries: 3,
            max_retries_down: 3,
            timeout: 5,
            name: '',
            http_method: 'GET',
            expected_codes: '200',
            url_path: '/',
            http_version: '1.0',
          },
        },
      ],
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Load Balancer name is required'),
      vip_network_id: Yup.string().required('Network is required'),
      createListener: Yup.string().required(
        'Please select whether to create a listener',
      ),
      listeners: Yup.array().when('createListener', {
        is: 'yes',
        then: (schema) =>
          schema.of(
            Yup.object({
              name: Yup.string().required('Listener name is required'),
              protocol: Yup.string().required('Protocol is required'),
              protocol_port: Yup.number()
                .required('Port is required')
                .min(1, 'Port must be between 1 and 65535')
                .max(65535, 'Port must be between 1 and 65535'),
              description: Yup.string().notRequired(),
              default_tls_container_ref: Yup.string().when('protocol', {
                is: 'TERMINATED_HTTPS',
                then: (schema) =>
                  schema.required(
                    'SSL Certificate is required for TERMINATED_HTTPS',
                  ),
                otherwise: (schema) => schema.notRequired(),
              }),
            }),
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      createPool: Yup.string().required(
        'Please select whether to create a pool',
      ),
      pools: Yup.array().when('createPool', {
        is: 'yes',
        then: (schema) =>
          schema.of(
            Yup.object({
              name: Yup.string().required('Pool name is required'),
              protocol: Yup.string().required('Protocol is required'),
              session_persistence: Yup.object({
                type: Yup.string().required('Session Persistence is required'),
                cookie_name: Yup.string().when('type', {
                  is: (value) => ['APP_COOKIE', 'HTTP_COOKIE'].includes(value),
                  then: (schema) =>
                    schema.required(
                      'Cookie name is required for APP_COOKIE or HTTP_COOKIE',
                    ),
                  otherwise: (schema) => schema.notRequired(),
                }),
                persistence_timeout: Yup.number().notRequired(),
                persistence_granularity: Yup.string().notRequired(),
              }).required('Session Persistence is required'),
              lb_algorithm: Yup.string().required('Algorithm is required'),
              createHealthMonitor: Yup.string().required(
                'Please select whether to create a health monitor',
              ),
              members: Yup.array().when('createHealthMonitor', {
                is: 'yes',
                then: (schema) =>
                  schema.of(
                    Yup.object({
                      address: Yup.string()
                        .matches(
                          /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/,
                          'Invalid IP address format',
                        )
                        .required('IP address is required'),
                      protocol_port: Yup.number()
                        .required('Port is required')
                        .min(1, 'Port must be between 1 and 65535')
                        .max(65535, 'Port must be between 1 and 65535'),
                      name: Yup.string().optional(),
                      weight: Yup.number()
                        .required('Weight is required')
                        .min(1, 'Weight must be between 1 and 256')
                        .max(256, 'Weight must be between 1 and 256'),
                    }),
                  ),
                otherwise: (schema) => schema.notRequired(),
              }),
              healthmonitor: Yup.object().when('createHealthMonitor', {
                is: 'yes',
                then: (schema) =>
                  schema.shape({
                    type: Yup.string().required(
                      'Health monitor type is required',
                    ),
                 delay: Yup.number()
                .required('Delay is required')
                .min(1, 'Delay must be at least 1 second')
                .test(
                  'delay-greater-than-or-equal-timeout',
                  'Delay must be equal to or greater than timeout',
                  function (value) {
                    const { timeout } = this.parent;

                    if (
                      value === undefined ||
                      value === null ||
                      timeout === undefined ||
                      timeout === null
                    ) {
                      return true;
                    }

                    return Number(value) >= Number(timeout);
                  },
                ),
                    max_retries: Yup.number()
                      .required('Max retries is required')
                      .min(1, 'Max retries must be at least 1'),
                    timeout: Yup.number()
                      .required('Timeout is required')
                      .min(1, 'Timeout must be at least 1 second'),
                    name: Yup.string().required(
                      'Health monitor name is required',
                    ),
                    http_method: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) =>
                        schema.required('HTTP Method is required'),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                    expected_codes: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) =>
                        schema.required('Expected Codes are required'),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                    url_path: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) => schema.required('URL Path is required'),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                    http_version: Yup.string().when('type', {
                      is: (value) => ['HTTP', 'HTTPS'].includes(value),
                      then: (schema) =>
                        schema
                          .required('HTTP Version is required')
                          .oneOf(
                            ['1.0', '1.1'],
                            'HTTP Version must be 1.0 or 1.1',
                          ),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                  }),
                otherwise: (schema) => schema.notRequired(),
              }),
            }),
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await createLoadBalanceForm.validateForm();
        const errors = createLoadBalanceForm.errors;
        const touched = createLoadBalanceForm.touched;

        const errorDetails = getErrorDetails(errors, touched);

        if (errorDetails.length > 0) {
          errorDetails.forEach(({ tab, field, message }) => {
            toast.error(`[${tab}] ${field}: ${message}`, {
              onClick: () => {
                setSelectedTab(tab);
                setTabPage(1);
              },
            });
          });
          setSelectedTab(errorDetails[0].tab);
          setTabPage(1);
          return;
        }

        const createLoadBalancerFormData = {
          name: values.name,
          project_id: projectId,
          vip_network_id: values.vip_network_id,
          vip_address: values.vip_address || undefined,
          pools:
            values.createPool === 'yes'
              ? values.pools.map((pool) => ({
                  ...pool,
                  session_persistence:
                    pool.session_persistence.type &&
                    pool.session_persistence.type !== 'none'
                      ? {
                          type: pool.session_persistence.type,
                          cookie_name:
                            pool.session_persistence.cookie_name || undefined,
                          persistence_timeout:
                            pool.session_persistence.persistence_timeout ||
                            undefined,
                          persistence_granularity:
                            pool.session_persistence.persistence_granularity ||
                            undefined,
                        }
                      : undefined,
                  members: pool.members.map((member) => {
                    const memberData = {
                      address: member.address,
                      protocol_port: member.protocol_port,
                      weight: member.weight,
                      backup: member.backup || undefined,
                      monitor_address: member.monitor_address || undefined,
                      monitor_port: member.monitor_port || undefined,
                    };
                    if (member.name) {
                      memberData.name = member.name;
                    }
                    return memberData;
                  }),
                  healthmonitor:
                    pool.createHealthMonitor === 'yes'
                      ? {
                          ...pool.healthmonitor,
                          http_method: ['HTTP', 'HTTPS'].includes(
                            pool.healthmonitor.type,
                          )
                            ? pool.healthmonitor.http_method
                            : undefined,
                          expected_codes: ['HTTP', 'HTTPS'].includes(
                            pool.healthmonitor.type,
                          )
                            ? pool.healthmonitor.expected_codes
                            : undefined,
                          url_path: ['HTTP', 'HTTPS'].includes(
                            pool.healthmonitor.type,
                          )
                            ? pool.healthmonitor.url_path
                            : undefined,
                          http_version: ['HTTP', 'HTTPS'].includes(
                            pool.healthmonitor.type,
                          )
                            ? pool.healthmonitor.http_version
                            : undefined,
                        }
                      : undefined,
                }))
              : [],
          listeners:
            values.createListener === 'yes'
              ? values.listeners.map((listener) => ({
                  ...listener,
                  allowed_cidrs: listener.allowed_cidrs
                    ? listener.allowed_cidrs
                        .split('\n')
                        .filter((cidr) => cidr.trim())
                    : undefined,
                  description: listener.description || undefined,
                  default_tls_container_ref:
                    listener.protocol === 'TERMINATED_HTTPS'
                      ? listener.default_tls_container_ref
                      : undefined,
                }))
              : [],
        };

      if (
        values.createPool === "yes" &&
        poolMemberUnitPrice === null
      ) {
        toast.error(
          "Unable to retrieve the pool member price for this zone."
        );
        return;
      }

      const totalCost = calculatePoolMemberCost(values);

      let currentWalletBalance = walletBalance;

      // The wallet can briefly be null when a project/region is switched.
      // Refresh it before deciding that the user needs Squad payment.
      if (currentWalletBalance === null) {
        try {
          setWalletLoading(true);

          const { payload: refreshedAccount } = await dispatch(
            getUserVirtualAccount(),
          );

          currentWalletBalance = getWalletBalance(refreshedAccount);
        } catch (error) {
          console.error('Unable to retrieve wallet balance:', error);
        } finally {
          setWalletLoading(false);
        }
      }

      // Do not interpret an unavailable wallet as a zero balance.
      if (currentWalletBalance === null) {
        toast.error(
          'Unable to retrieve your wallet balance. Please try again.',
        );
        return;
      }

      if (totalCost > currentWalletBalance) {
        setShowPaymentModal(true);
        return;
      }

      // Proceed with load balancer creation
      const { payload } = await dispatch(
        createLoadBalancer(createLoadBalancerFormData)
      );

      if (payload?.statuscode === "00") {
        setTimeout(() => {
          navigate("/manageLoadBalancers");
        }, 1000);
      }
      } catch (error) {
        console.error(error);
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          error?.statusmessage ||
          error?.payload?.data?.message ||
          'An unexpected error occurred during submission';
        toast.error(errorMsg, { theme: 'colored' });
      }
    },
  });

  const totalCost = calculatePoolMemberCost(
  createLoadBalanceForm.values
);

  const loadBalancerInputs = [
    {
      type: 'text',
      element: 'input',
      name: 'name',
      label: 'Name *',
      placeholder: 'Enter load balancer name',
      value: createLoadBalanceForm.values.name,
      colSpan: 1,
      onChange: createLoadBalanceForm.handleChange,
      onBlur: createLoadBalanceForm.handleBlur,
      onError:
        createLoadBalanceForm.touched.name && createLoadBalanceForm.errors.name
          ? createLoadBalanceForm.errors.name
          : null,
    },
    {
      type: 'text',
      element: 'input',
      name: 'vip_address',
      label: 'IP Address',
      placeholder: 'Optional IP address',
      value: createLoadBalanceForm.values.vip_address,
      colSpan: 1,
      onChange: createLoadBalanceForm.handleChange,
      onBlur: createLoadBalanceForm.handleBlur,
      onError:
        createLoadBalanceForm.touched.vip_address &&
        createLoadBalanceForm.errors.vip_address
          ? createLoadBalanceForm.errors.vip_address
          : null,
    },
    {
      type: 'text',
      element: 'select',
      name: 'vip_network_id',
      label: 'Network *',
      placeholder: 'Select network',
      options: networkListOptions,
      value: createLoadBalanceForm.values.vip_network_id,
      colSpan: 2,
      onChange: createLoadBalanceForm.handleChange,
      onBlur: createLoadBalanceForm.handleBlur,
      onError:
        createLoadBalanceForm.touched.vip_network_id &&
        createLoadBalanceForm.errors.vip_network_id
          ? createLoadBalanceForm.errors.vip_network_id
          : null,
    },
  ];

  const listenerInputs = useMemo(
    () => [
      {
        type: 'choice',
        element: 'choice',
        name: 'createListener',
        label: 'Create Listener? *',
        choices: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
        value: createLoadBalanceForm.values.createListener,
        colSpan: 2,
        onChange: (value) => {
          createLoadBalanceForm.setFieldValue('createListener', value);
          if (value === 'yes') {
            createLoadBalanceForm.setFieldValue(
              'pools[0].protocol',
              createLoadBalanceForm.values.listeners[0].protocol,
            );
          }
        },
        onBlur: createLoadBalanceForm.handleBlur,
        onError:
          createLoadBalanceForm.touched.createListener &&
          createLoadBalanceForm.errors.createListener
            ? createLoadBalanceForm.errors.createListener
            : null,
      },
      ...(createLoadBalanceForm.values.createListener === 'yes'
        ? [
            {
              type: 'text',
              element: 'input',
              name: 'listeners[0].name',
              label: 'Name *',
              placeholder: 'Enter listener name',
              value: createLoadBalanceForm.values.listeners[0].name,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]?.name &&
                createLoadBalanceForm.errors.listeners?.[0]?.name
                  ? createLoadBalanceForm.errors.listeners[0].name
                  : null,
            },
            {
              type: 'text',
              element: 'input',
              name: 'listeners[0].description',
              label: 'Description',
              placeholder: 'Enter listener description (optional)',
              value: createLoadBalanceForm.values.listeners[0].description,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]?.description &&
                createLoadBalanceForm.errors.listeners?.[0]?.description
                  ? createLoadBalanceForm.errors.listeners[0].description
                  : null,
            },
            {
              type: 'text',
              element: 'select',
              name: 'listeners[0].protocol',
              label: 'Protocol *',
              placeholder: 'Select protocol',
              options: [
                { label: 'Select protocol', value: '' },
                { label: 'HTTP', value: 'HTTP' },
                { label: 'HTTPS', value: 'HTTPS' },
                { label: 'TERMINATED_HTTPS', value: 'TERMINATED_HTTPS' },
                { label: 'SCTP', value: 'SCTP' },
                { label: 'TCP', value: 'TCP' },
                { label: 'UDP', value: 'UDP' },
              ],
              value: createLoadBalanceForm.values.listeners[0].protocol,
              colSpan: 1,
              onChange: (e) => {
                createLoadBalanceForm.handleChange(e);
                createLoadBalanceForm.setFieldValue(
                  'pools[0].protocol',
                  e.target.value === 'TERMINATED_HTTPS'
                    ? 'HTTP'
                    : e.target.value,
                );
                if (e.target.value !== 'TERMINATED_HTTPS') {
                  createLoadBalanceForm.setFieldValue(
                    'listeners[0].default_tls_container_ref',
                    '',
                  );
                }
              },
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]?.protocol &&
                createLoadBalanceForm.errors.listeners?.[0]?.protocol
                  ? createLoadBalanceForm.errors.listeners[0].protocol
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'listeners[0].protocol_port',
              label: 'Port *',
              placeholder: '443',
              value: createLoadBalanceForm.values.listeners[0].protocol_port,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]?.protocol_port &&
                createLoadBalanceForm.errors.listeners?.[0]?.protocol_port
                  ? createLoadBalanceForm.errors.listeners[0].protocol_port
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'listeners[0].timeout_client_data',
              label: 'Client Data Timeout',
              placeholder: '5000',
              value:
                createLoadBalanceForm.values.listeners[0].timeout_client_data,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]
                  ?.timeout_client_data &&
                createLoadBalanceForm.errors.listeners?.[0]?.timeout_client_data
                  ? createLoadBalanceForm.errors.listeners[0]
                      .timeout_client_data
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'listeners[0].timeout_tcp_inspect',
              label: 'TCP Inspect Timeout',
              placeholder: '0',
              value:
                createLoadBalanceForm.values.listeners[0].timeout_tcp_inspect,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]
                  ?.timeout_tcp_inspect &&
                createLoadBalanceForm.errors.listeners?.[0]?.timeout_tcp_inspect
                  ? createLoadBalanceForm.errors.listeners[0]
                      .timeout_tcp_inspect
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'listeners[0].timeout_member_connect',
              label: 'Member Connect Timeout',
              placeholder: '5000',
              value:
                createLoadBalanceForm.values.listeners[0]
                  .timeout_member_connect,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]
                  ?.timeout_member_connect &&
                createLoadBalanceForm.errors.listeners?.[0]
                  ?.timeout_member_connect
                  ? createLoadBalanceForm.errors.listeners[0]
                      .timeout_member_connect
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'listeners[0].timeout_member_data',
              label: 'Member Data Timeout',
              placeholder: '50000',
              value:
                createLoadBalanceForm.values.listeners[0].timeout_member_data,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]
                  ?.timeout_member_data &&
                createLoadBalanceForm.errors.listeners?.[0]?.timeout_member_data
                  ? createLoadBalanceForm.errors.listeners[0]
                      .timeout_member_data
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'listeners[0].connection_limit',
              label: 'Connection Limit',
              placeholder: '-1',
              value: createLoadBalanceForm.values.listeners[0].connection_limit,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.listeners?.[0]
                  ?.connection_limit &&
                createLoadBalanceForm.errors.listeners?.[0]?.connection_limit
                  ? createLoadBalanceForm.errors.listeners[0].connection_limit
                  : null,
            },
          ]
        : []),
    ],
    [
      createLoadBalanceForm.values.createListener,
      createLoadBalanceForm.values.listeners,
      createLoadBalanceForm.touched,
      createLoadBalanceForm.errors,
      createLoadBalanceForm.handleChange,
      createLoadBalanceForm.handleBlur,
      createLoadBalanceForm.setFieldValue,
    ],
  );

  const poolInputs = useMemo(
    () => [
      {
        type: 'choice',
        element: 'choice',
        name: 'createPool',
        label: 'Create Pool? *',
        choices: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
        value: createLoadBalanceForm.values.createPool,
        colSpan: 2,
        onChange: (value) =>
          createLoadBalanceForm.setFieldValue('createPool', value),
        onBlur: createLoadBalanceForm.handleBlur,
        onError:
          createLoadBalanceForm.touched.createPool &&
          createLoadBalanceForm.errors.createPool
            ? createLoadBalanceForm.errors.createPool
            : null,
      },
      ...(createLoadBalanceForm.values.createPool === 'yes'
        ? [
            {
              type: 'text',
              element: 'input',
              name: 'pools[0].name',
              label: 'Name *',
              placeholder: 'Enter pool name',
              value: createLoadBalanceForm.values.pools[0].name,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.name &&
                createLoadBalanceForm.errors.pools?.[0]?.name
                  ? createLoadBalanceForm.errors.pools[0].name
                  : null,
            },
            {
              type: 'text',
              element: 'select',
              name: 'pools[0].session_persistence.type',
              label: 'Session Persistence *',
              placeholder: 'Select session persistence',
              options: [
                { label: 'Select session persistence', value: '' },
                { label: 'None', value: 'none' },
                { label: 'SOURCE IP', value: 'SOURCE_IP' },
                { label: 'HTTP_COOKIE', value: 'HTTP_COOKIE' },
                { label: 'APP_COOKIE', value: 'APP_COOKIE' },
              ],
              value:
                createLoadBalanceForm.values.pools[0].session_persistence.type,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.session_persistence
                  ?.type &&
                createLoadBalanceForm.errors.pools?.[0]?.session_persistence
                  ?.type
                  ? createLoadBalanceForm.errors.pools[0].session_persistence
                      .type
                  : null,
            },
            ...(createLoadBalanceForm.values.pools[0].session_persistence
              .type === 'APP_COOKIE' ||
            createLoadBalanceForm.values.pools[0].session_persistence.type ===
              'HTTP_COOKIE'
              ? [
                  {
                    type: 'text',
                    element: 'input',
                    name: 'pools[0].session_persistence.cookie_name',
                    label: 'Cookie Name *',
                    placeholder: 'Enter cookie name',
                    value:
                      createLoadBalanceForm.values.pools[0].session_persistence
                        .cookie_name,
                    colSpan: 1,
                    onChange: createLoadBalanceForm.handleChange,
                    onBlur: createLoadBalanceForm.handleBlur,
                    onError:
                      createLoadBalanceForm.touched.pools?.[0]
                        ?.session_persistence?.cookie_name &&
                      createLoadBalanceForm.errors.pools?.[0]
                        ?.session_persistence?.cookie_name
                        ? createLoadBalanceForm.errors.pools[0]
                            .session_persistence.cookie_name
                        : null,
                  },
                ]
              : []),
            {
              type: 'number',
              element: 'input',
              name: 'pools[0].session_persistence.persistence_timeout',
              label: 'Persistence Timeout',
              placeholder: 'Enter persistence timeout (optional)',
              value:
                createLoadBalanceForm.values.pools[0].session_persistence
                  .persistence_timeout,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.session_persistence
                  ?.persistence_timeout &&
                createLoadBalanceForm.errors.pools?.[0]?.session_persistence
                  ?.persistence_timeout
                  ? createLoadBalanceForm.errors.pools[0].session_persistence
                      .persistence_timeout
                  : null,
            },
            {
              type: 'text',
              element: 'input',
              name: 'pools[0].session_persistence.persistence_granularity',
              label: 'Persistence Granularity',
              placeholder: 'Enter persistence granularity (optional)',
              value:
                createLoadBalanceForm.values.pools[0].session_persistence
                  .persistence_granularity,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.session_persistence
                  ?.persistence_granularity &&
                createLoadBalanceForm.errors.pools?.[0]?.session_persistence
                  ?.persistence_granularity
                  ? createLoadBalanceForm.errors.pools[0].session_persistence
                      .persistence_granularity
                  : null,
            },
            {
              type: 'text',
              element: 'select',
              name: 'pools[0].lb_algorithm',
              label: 'Algorithm *',
              placeholder: 'Select algorithm',
              options: [
                { label: 'Select algorithm', value: '' },
                { label: 'LEAST CONNECTIONS', value: 'LEAST_CONNECTIONS' },
                { label: 'ROUND ROBIN', value: 'ROUND_ROBIN' },
                { label: 'SOURCE IP', value: 'SOURCE_IP' },
              ],
              value: createLoadBalanceForm.values.pools[0].lb_algorithm,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.lb_algorithm &&
                createLoadBalanceForm.errors.pools?.[0]?.lb_algorithm
                  ? createLoadBalanceForm.errors.pools[0].lb_algorithm
                  : null,
            },
          ]
        : []),
    ],
    [
      createLoadBalanceForm.values.createPool,
      createLoadBalanceForm.values.pools,
      createLoadBalanceForm.touched,
      createLoadBalanceForm.errors,
      createLoadBalanceForm.handleChange,
      createLoadBalanceForm.handleBlur,
      createLoadBalanceForm.setFieldValue,
    ],
  );

  const monitorInputs = useMemo(
    () => [
      {
        type: 'choice',
        element: 'choice',
        name: 'pools[0].createHealthMonitor',
        label: 'Create Health Monitor? *',
        choices: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
        value: createLoadBalanceForm.values.pools[0].createHealthMonitor,
        colSpan: 2,
        onChange: (value) =>
          createLoadBalanceForm.setFieldValue(
            'pools[0].createHealthMonitor',
            value,
          ),
        onBlur: createLoadBalanceForm.handleBlur,
        onError:
          createLoadBalanceForm.touched.pools?.[0]?.createHealthMonitor &&
          createLoadBalanceForm.errors.pools?.[0]?.createHealthMonitor
            ? createLoadBalanceForm.errors.pools[0].createHealthMonitor
            : null,
      },
      ...(createLoadBalanceForm.values.createPool === 'yes' &&
      createLoadBalanceForm.values.pools[0].createHealthMonitor === 'yes'
        ? [
            {
              type: 'text',
              element: 'input',
              name: 'pools[0].healthmonitor.name',
              label: 'Name *',
              placeholder: 'Enter health monitor name',
              value: createLoadBalanceForm.values.pools[0].healthmonitor.name,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.healthmonitor?.name &&
                createLoadBalanceForm.errors.pools?.[0]?.healthmonitor?.name
                  ? createLoadBalanceForm.errors.pools[0].healthmonitor.name
                  : null,
            },
            {
              type: 'text',
              element: 'select',
              name: 'pools[0].healthmonitor.type',
              label: 'Type *',
              placeholder: 'Select type',
              options: [
                { label: 'Select type', value: '' },
                { label: 'HTTP', value: 'HTTP' },
                { label: 'HTTPS', value: 'HTTPS' },
                { label: 'PING', value: 'PING' },
                { label: 'SCTP', value: 'SCTP' },
                { label: 'TCP', value: 'TCP' },
                { label: 'TLS-HELLO', value: 'TLS-HELLO' },
                { label: 'UDP-CONNECT', value: 'UDP-CONNECT' },
              ],
              value: createLoadBalanceForm.values.pools[0].healthmonitor.type,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.healthmonitor?.type &&
                createLoadBalanceForm.errors.pools?.[0]?.healthmonitor?.type
                  ? createLoadBalanceForm.errors.pools[0].healthmonitor.type
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'pools[0].healthmonitor.delay',
              label: 'Delay (sec) *',
              placeholder: '3',
              value: createLoadBalanceForm.values.pools[0].healthmonitor.delay,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                  ?.delay &&
                createLoadBalanceForm.errors.pools?.[0]?.healthmonitor?.delay
                  ? createLoadBalanceForm.errors.pools[0].healthmonitor.delay
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'pools[0].healthmonitor.max_retries',
              label: 'Max Retries *',
              placeholder: '2',
              value:
                createLoadBalanceForm.values.pools[0].healthmonitor.max_retries,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                  ?.max_retries &&
                createLoadBalanceForm.errors.pools?.[0]?.healthmonitor
                  ?.max_retries
                  ? createLoadBalanceForm.errors.pools[0].healthmonitor
                      .max_retries
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'pools[0].healthmonitor.max_retries_down',
              label: 'Max Retries Down',
              placeholder: '3',
              value:
                createLoadBalanceForm.values.pools[0].healthmonitor
                  ?.max_retries_down,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                  ?.max_retries_down &&
                createLoadBalanceForm.errors.pools?.[0]?.healthmonitor
                  ?.max_retries_down
                  ? createLoadBalanceForm.errors.pools[0].healthmonitor
                      .max_retries_down
                  : null,
            },
            {
              type: 'number',
              element: 'input',
              name: 'pools[0].healthmonitor.timeout',
              label: 'Timeout (sec) *',
              placeholder: '1',
              value:
                createLoadBalanceForm.values.pools[0].healthmonitor.timeout,
              colSpan: 1,
              onChange: createLoadBalanceForm.handleChange,
              onBlur: createLoadBalanceForm.handleBlur,
              onError:
                createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                  ?.timeout &&
                createLoadBalanceForm.errors.pools?.[0]?.healthmonitor?.timeout
                  ? createLoadBalanceForm.errors.pools[0].healthmonitor.timeout
                  : null,
            },
            ...(createLoadBalanceForm.values.listeners[0].protocol ===
            'TERMINATED_HTTPS'
              ? [
                  {
                    type: 'text',
                    element: 'select',
                    name: 'listeners[0].default_tls_container_ref',
                    label: 'SSL Certificates *',
                    placeholder: 'Select certificate',
                    options: [
                      { value: '', label: 'Select certificate' },
                      ...certificates.map((certificate) => ({
                        value: certificate?.secret_ref,
                        label:
                          certificate?.name === null
                            ? 'N/A'
                            : certificate?.name,
                      })),
                    ],
                    value:
                      createLoadBalanceForm.values.listeners[0]
                        .default_tls_container_ref,
                    colSpan: 1,
                    onChange: createLoadBalanceForm.handleChange,
                    onBlur: createLoadBalanceForm.handleBlur,
                    onError:
                      createLoadBalanceForm.touched.listeners?.[0]
                        ?.default_tls_container_ref &&
                      createLoadBalanceForm.errors.listeners?.[0]
                        ?.default_tls_container_ref
                        ? createLoadBalanceForm.errors.listeners[0]
                            .default_tls_container_ref
                        : null,
                  },
                ]
              : []),
            ...(createLoadBalanceForm.values.pools[0].healthmonitor.type ===
              'HTTP' ||
            createLoadBalanceForm.values.pools[0].healthmonitor.type === 'HTTPS'
              ? [
                  {
                    type: 'text',
                    element: 'select',
                    name: 'pools[0].healthmonitor.http_method',
                    label: 'HTTP Method *',
                    placeholder: 'Select HTTP method',
                    options: [
                      { label: 'CONNECT', value: 'CONNECT' },
                      { label: 'DELETE', value: 'DELETE' },
                      { label: 'GET', value: 'GET' },
                      { label: 'HEAD', value: 'HEAD' },
                      { label: 'OPTIONS', value: 'OPTIONS' },
                      { label: 'PATCH', value: 'PATCH' },
                      { label: 'POST', value: 'POST' },
                      { label: 'PUT', value: 'PUT' },
                      { label: 'TRACE', value: 'TRACE' },
                    ],
                    value:
                      createLoadBalanceForm.values.pools[0].healthmonitor
                        .http_method,
                    colSpan: 1,
                    onChange: createLoadBalanceForm.handleChange,
                    onBlur: createLoadBalanceForm.handleBlur,
                    onError:
                      createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                        ?.http_method &&
                      createLoadBalanceForm.errors.pools?.[0]?.healthmonitor
                        ?.http_method
                        ? createLoadBalanceForm.errors.pools[0].healthmonitor
                            .http_method
                        : null,
                  },
                  {
                    type: 'text',
                    element: 'input',
                    name: 'pools[0].healthmonitor.expected_codes',
                    label: 'Expected Codes *',
                    placeholder: 'e.g., 200, 200-204',
                    value:
                      createLoadBalanceForm.values.pools[0].healthmonitor
                        .expected_codes,
                    colSpan: 1,
                    onChange: createLoadBalanceForm.handleChange,
                    onBlur: createLoadBalanceForm.handleBlur,
                    onError:
                      createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                        ?.expected_codes &&
                      createLoadBalanceForm.errors.pools?.[0]?.healthmonitor
                        ?.expected_codes
                        ? createLoadBalanceForm.errors.pools[0].healthmonitor
                            .expected_codes
                        : null,
                  },
                  {
                    type: 'text',
                    element: 'input',
                    name: 'pools[0].healthmonitor.url_path',
                    label: 'URL Path *',
                    placeholder: 'e.g., /health',
                    value:
                      createLoadBalanceForm.values.pools[0].healthmonitor
                        .url_path,
                    colSpan: 1,
                    onChange: createLoadBalanceForm.handleChange,
                    onBlur: createLoadBalanceForm.handleBlur,
                    onError:
                      createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                        ?.url_path &&
                      createLoadBalanceForm.errors.pools?.[0]?.healthmonitor
                        ?.url_path
                        ? createLoadBalanceForm.errors.pools[0].healthmonitor
                            .url_path
                        : null,
                  },
                  {
                    type: 'text',
                    element: 'select',
                    name: 'pools[0].healthmonitor.http_version',
                    label: 'HTTP Version *',
                    placeholder: 'Select HTTP version',
                    options: [
                      { label: '1.0', value: '1.0' },
                      { label: '1.1', value: '1.1' },
                    ],
                    value:
                      createLoadBalanceForm.values.pools[0].healthmonitor
                        .http_version,
                    colSpan: 1,
                    onChange: createLoadBalanceForm.handleChange,
                    onBlur: createLoadBalanceForm.handleBlur,
                    onError:
                      createLoadBalanceForm.touched.pools?.[0]?.healthmonitor
                        ?.http_version &&
                      createLoadBalanceForm.errors.pools?.[0]?.healthmonitor
                        ?.http_version
                        ? createLoadBalanceForm.errors.pools[0].healthmonitor
                            .http_version
                        : null,
                  },
                ]
              : []),
          ]
        : []),
    ],
    [
      createLoadBalanceForm.values.createPool,
      createLoadBalanceForm.values.pools,
      createLoadBalanceForm.values.listeners,
      createLoadBalanceForm.touched,
      createLoadBalanceForm.errors,
      createLoadBalanceForm.handleChange,
      createLoadBalanceForm.handleBlur,
      createLoadBalanceForm.setFieldValue,
      certificates,
    ],
  );

  const tabConfigs = {
    'Load Balancer Details *': {
      title: 'Provide the details for the load balancer',
      inputs: loadBalancerInputs,
    },
    'Listener Details *': {
      title: 'Provide the details for the listener',
      inputs: listenerInputs,
    },
    'Pool Details *': {
      title: 'Provide the details for the pool',
      inputs: poolInputs,
    },
    'Pool Members': {
      title: 'Provide the details for pool members',
      inputs: [],
    },
    'Monitor Details *': {
      title: 'Provide the details for the monitor',
      inputs: monitorInputs,
    },
  };

  const addMember = () => {
    const newMembers = [
      ...createLoadBalanceForm.values.pools[0].members,
      {
        address: '',
        protocol_port: '',
        name: '',
        weight: '',
        backup: false,
        monitor_address: '',
        monitor_port: '',
      },
    ];
    createLoadBalanceForm.setFieldValue('pools[0].members', newMembers);
  };

  const removeMember = (index) => {
    const newMembers = createLoadBalanceForm.values.pools[0].members.filter(
      (_, i) => i !== index,
    );
    createLoadBalanceForm.setFieldValue('pools[0].members', newMembers);
  };

  useEffect(() => {
    const validateTab = async () => {
      const errors = await createLoadBalanceForm.validateForm();
      const values = createLoadBalanceForm.values;
      const touched = createLoadBalanceForm.touched;

      const tabValidation = {
        'Load Balancer Details *':
          values.name &&
          values.vip_network_id &&
          !errors.name &&
          !errors.vip_network_id,
        'Listener Details *':
          !errors.createListener &&
          (values.createListener === 'no' ||
            (values.listeners[0].name &&
              values.listeners[0].protocol &&
              values.listeners[0].protocol_port &&
              !errors.listeners?.[0]?.name &&
              !errors.listeners?.[0]?.protocol &&
              !errors.listeners?.[0]?.protocol_port &&
              (values.listeners[0].protocol !== 'TERMINATED_HTTPS' ||
                (values.listeners[0].default_tls_container_ref &&
                  !errors.listeners?.[0]?.default_tls_container_ref)))),
        'Pool Details *':
          !errors.createPool &&
          (values.createPool === 'no' ||
            (values.pools[0].name &&
              values.pools[0].session_persistence.type &&
              values.pools[0].lb_algorithm &&
              values.pools[0].protocol &&
              !errors.pools?.[0]?.name &&
              !errors.pools?.[0]?.session_persistence?.type &&
              !errors.pools?.[0]?.lb_algorithm &&
              !errors.pools?.[0]?.protocol)),
        'Pool Members':
          values.createPool === 'no' ||
          values.pools[0].createHealthMonitor === 'no' ||
          values.pools[0].members.every(
            (member) =>
              member.address &&
              member.protocol_port &&
              member.weight &&
              !errors.pools?.[0]?.members?.find(
                (m) => m?.address === member?.address,
              ),
          ),
        'Monitor Details *':
          values.createPool === 'no' ||
          (!errors.pools?.[0]?.createHealthMonitor &&
            (values.pools[0].createHealthMonitor === 'no' ||
              (values.pools[0].healthmonitor.name &&
                values.pools[0].healthmonitor.type &&
                values.pools[0].healthmonitor.delay &&
                values.pools[0].healthmonitor.max_retries &&
                values.pools[0].healthmonitor.timeout &&
                (!['HTTP', 'HTTPS'].includes(
                  values.pools[0].healthmonitor.type,
                ) ||
                  (values.pools[0].healthmonitor.http_method &&
                    values.pools[0].healthmonitor.expected_codes &&
                    values.pools[0].healthmonitor.url_path &&
                    values.pools[0].healthmonitor.http_version)) &&
                !errors.pools?.[0]?.healthmonitor?.name &&
                !errors.pools?.[0]?.healthmonitor?.type &&
                !errors.pools?.[0]?.healthmonitor?.delay &&
                !errors.pools?.[0]?.healthmonitor?.max_retries &&
                !errors.pools?.[0]?.healthmonitor?.timeout &&
                !errors.pools?.[0]?.healthmonitor?.http_method &&
                !errors.pools?.[0]?.healthmonitor?.expected_codes &&
                !errors.pools?.[0]?.healthmonitor?.url_path &&
                !errors.pools?.[0]?.healthmonitor?.http_version))),
      };

      setIsTabValid(tabValidation);
    };
    validateTab();
  }, [
    createLoadBalanceForm.values,
    createLoadBalanceForm.errors,
    createLoadBalanceForm.touched,
  ]);

  const navigateToPrevious = async () => {
    await createLoadBalanceForm.validateForm();
    const errors = createLoadBalanceForm.errors;
    const touched = createLoadBalanceForm.touched;
    const errorDetails = getErrorDetails(errors, touched).filter(
      (error) => error.tab === selectedTab,
    );

    if (errorDetails.length > 0) {
      errorDetails.forEach(({ field, message }) => {
        toast.error(`[${selectedTab}] ${field}: ${message}`);
      });
      return;
    }

    const tabIndex = tabsBalancers.indexOf(selectedTab);
    if (selectedTab === 'Pool Members' && tabPage > 1) {
      setTabPage(tabPage - 1);
    } else if (tabIndex > 0) {
      setSelectedTab(tabsBalancers[tabIndex - 1]);
      setTabPage(1);
    }
  };

  const navigateToNext = async () => {
    await createLoadBalanceForm.validateForm();
    const errors = createLoadBalanceForm.errors;
    const touched = createLoadBalanceForm.touched;
    const errorDetails = getErrorDetails(errors, touched).filter(
      (error) => error.tab === selectedTab,
    );

    if (errorDetails.length > 0) {
      errorDetails.forEach(({ field, message }) => {
        toast.error(`[${selectedTab}] ${field}: ${message}`);
      });
      return;
    }

    const tabIndex = tabsBalancers.indexOf(selectedTab);
    if (
      selectedTab === 'Pool Members' &&
      tabPage <
        Math.ceil(
          createLoadBalanceForm.values.pools[0].members.length /
            entriesTabPerPage,
        )
    ) {
      setTabPage(tabPage + 1);
    } else if (tabIndex < tabsBalancers.length - 1) {
      setSelectedTab(tabsBalancers[tabIndex + 1]);
      setTabPage(1);
    }
  };

  const lastEntryIndex = tabPage * entriesTabPerPage;
  const firstEntryIndex = lastEntryIndex - entriesTabPerPage;
  const visibleEntries = createLoadBalanceForm.values.pools[0].members.slice(
    firstEntryIndex,
    lastEntryIndex,
  );
  const totalTabPages = Math.ceil(
    createLoadBalanceForm.values.pools[0].members.length / entriesTabPerPage,
  );

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-xl font-medium text-primaryBlue">
        Create Load Balancer
      </p>

      <div className="w-full flex flex-col justify-center lg:justify-start lg:items-start items-center lg:grid grid-cols-1 lg:grid-cols-12 bg-white rounded-lg !rounded-r-[initial] min-h-screen mt-6">
        <div className="lg:col-span-3 flex flex-wrap lg:flex-nowrap lg:flex-col justify-center lg:justify-start items-center lg:items-start gap-4 lg:min-h-screen border-b border-b-gray-200 lg:border-r lg:border-r-gray-200 pt-12">
          {tabsBalancers.map((tab, index) => (
            <button
              key={index}
              className={`w-fit md:w-[90%] ${
                selectedTab === tab
                  ? 'bg-primaryBlue text-white'
                  : 'bg-transparent'
              } py-4 px-6 rounded-md ${
                selectedTab === tab ? 'text-white' : 'text-primaryBlue'
              } text-sm font-medium`}
              onClick={() => {
                setSelectedTab(tab);
                setTabPage(1);
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="w-full lg:col-span-9 flex flex-col justify-start items-start gap-2 py-10 px-4 lg:px-8 h-[600px] overflow-y-auto">
          <form
            onSubmit={createLoadBalanceForm.handleSubmit}
            className="w-full"
          >
            {selectedTab !== 'Pool Members' ? (
              <>
                {tabConfigs[selectedTab] && (
                  <TabContent
                    title={tabConfigs[selectedTab].title}
                    inputs={tabConfigs[selectedTab].inputs}
                  />
                )}
              </>
            ) : (
              <div className="w-full">
                <h3 className="text-gray-800 text-xl font-semibold mb-10">
                  {tabConfigs[selectedTab]?.title}
                </h3>
                <div className="w-full">
                  <h3 className="text-gray-700 text-lg font-semibold mb-4">
                    Pool Members
                  </h3>

                  {createLoadBalanceForm.values.createPool === "yes" && (
                      <div className="mb-6 p-4 bg-blue-100 dark:bg-blue-900 rounded-md">
                        <p className="text-gray-800 dark:text-gray-200 text-sm">
                          <span className="font-semibold">Pricing:</span> Each
                          pool member/node costs{' '}
                          {unitPriceLoading ? (
                    <span className="font-bold">
                      Loading price...
                    </span>
                  ) : poolMemberUnitPrice !== null ? (
                    <span className="font-bold">
                      {poolMemberUnitPrice.toLocaleString("en-US", {
                        style: "currency",
                        currency: currency === "NGN" ? "NGN" : "USD",
                      })}
                    </span>
                  ) : (
                    <span className="font-bold text-red-500">
                      Price unavailable
                    </span>
                  )}
                        </p>
                        <p className="text-gray-800 dark:text-gray-200 text-sm mt-2">
                          You have added{' '}
                          <span className="font-bold">
                            {
                              createLoadBalanceForm.values.pools[0].members
                                .length
                            }
                          </span>{' '}
                      member(s). Estimated monthly cost:{' '}

                    {unitPriceLoading ? (
                      <span className="font-bold">
                        Loading price...
                      </span>
                    ) : poolMemberUnitPrice !== null ? (
                      <span className="font-bold">
                        {totalCost.toLocaleString('en-US', {
                          style: 'currency',
                          currency: currency === 'NGN' ? 'NGN' : 'USD',
                        })}
                      </span>
                    ) : (
                      <span className="font-bold text-red-500">
                        Price unavailable
                      </span>
                    )}
                    </p>
                  </div>
                  )}
                  {createLoadBalanceForm.values.createPool === 'yes' &&
                  createLoadBalanceForm.values.pools[0].createHealthMonitor ===
                    'yes' ? (
                    visibleEntries.map((member, index) => (
                      <div
                        key={index + firstEntryIndex}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 border-b pb-4"
                      >
                        <Input
                          type="text"
                          label="IP Address *"
                          name={`pools[0].members[${index + firstEntryIndex}].address`}
                          placeholder="e.g., 192.0.2.51"
                          value={member.address}
                          onChange={createLoadBalanceForm.handleChange}
                          onBlur={createLoadBalanceForm.handleBlur}
                          onError={
                            createLoadBalanceForm.touched.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.address &&
                            createLoadBalanceForm.errors.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.address
                              ? createLoadBalanceForm.errors.pools[0].members[
                                  index + firstEntryIndex
                                ].address
                              : null
                          }
                        />
                        <Input
                          type="number"
                          label="Port *"
                          name={`pools[0].members[${index + firstEntryIndex}].protocol_port`}
                          placeholder="e.g., 80"
                          value={member.protocol_port}
                          onChange={createLoadBalanceForm.handleChange}
                          onBlur={createLoadBalanceForm.handleBlur}
                          onError={
                            createLoadBalanceForm.touched.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.protocol_port &&
                            createLoadBalanceForm.errors.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.protocol_port
                              ? createLoadBalanceForm.errors.pools[0].members[
                                  index + firstEntryIndex
                                ].protocol_port
                              : null
                          }
                        />
                        <Input
                          type="text"
                          label="Name"
                          name={`pools[0].members[${index + firstEntryIndex}].name`}
                          placeholder="e.g., Member 1 (optional)"
                          value={member.name}
                          onChange={createLoadBalanceForm.handleChange}
                          onBlur={createLoadBalanceForm.handleBlur}
                          onError={
                            createLoadBalanceForm.touched.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.name &&
                            createLoadBalanceForm.errors.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.name
                              ? createLoadBalanceForm.errors.pools[0].members[
                                  index + firstEntryIndex
                                ].name
                              : null
                          }
                        />
                        <Input
                          type="number"
                          label="Weight *"
                          name={`pools[0].members[${index + firstEntryIndex}].weight`}
                          placeholder="1"
                          value={member.weight}
                          onChange={createLoadBalanceForm.handleChange}
                          onBlur={createLoadBalanceForm.handleBlur}
                          onError={
                            createLoadBalanceForm.touched.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.weight &&
                            createLoadBalanceForm.errors.pools?.[0]?.members?.[
                              index + firstEntryIndex
                            ]?.weight
                              ? createLoadBalanceForm.errors.pools[0].members[
                                  index + firstEntryIndex
                                ].weight
                              : null
                          }
                        />
                        <button
                          type="button"
                          className="text-red-500 text-sm"
                          onClick={() => removeMember(index + firstEntryIndex)}
                          disabled={
                            createLoadBalanceForm.values.pools[0].members
                              .length === 1
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">
                      Pool members are not required because pool or health
                      monitor creation is disabled.
                    </p>
                  )}
                  {createLoadBalanceForm.values.createPool === 'yes' &&
                    createLoadBalanceForm.values.pools[0]
                      .createHealthMonitor === 'yes' && (
                      <>
                        <button
                          type="button"
                          className="py-2 px-4 rounded-md text-sm border border-primaryBlue text-primaryBlue cursor-pointer"
                          onClick={addMember}
                        >
                          Add Member
                        </button>
                        <div className="flex justify-between items-center mt-4">
                          <p className="text-gray-400 text-sm">
                            Showing{' '}
                            <span className="font-medium text-black dark:text-white">
                              {firstEntryIndex + 1}
                            </span>{' '}
                            to{' '}
                            <span className="font-medium text-black dark:text-white">
                              {Math.min(
                                lastEntryIndex,
                                createLoadBalanceForm.values.pools[0].members
                                  .length,
                              )}
                            </span>{' '}
                            of{' '}
                            <span className="font-medium text-black dark:text-white">
                              {
                                createLoadBalanceForm.values.pools[0].members
                                  .length
                              }
                            </span>{' '}
                            members
                          </p>
                          <div className="flex gap-3">
                            <button
                              className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                                tabPage === 1 ? 'opacity-50' : 'opacity-100'
                              }`}
                              onClick={navigateToPrevious}
                              disabled={tabPage === 1}
                              type="button"
                            >
                              Previous
                            </button>
                            <button
                              className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${
                                tabPage === totalTabPages
                                  ? 'opacity-50'
                                  : 'opacity-100'
                              }`}
                              onClick={navigateToNext}
                              disabled={tabPage === totalTabPages}
                              type="button"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                  <LoadBalancerInstances />
                </div>
              </div>
            )}
          </form>
        </div>

        {selectedTab === tabsBalancers[tabsBalancers.length - 1] && totalCost > 0 && (
          <div className="lg:col-span-12 border-t border-gray-200 w-full flex items-center justify-between px-6 py-3 bg-primaryBlue/5 dark:bg-primaryBlue/10">
            <span className="text-sm lg:text-lg font-bold text-gray-500 dark:text-gray-400">Estimated Pool Member Cost</span>
            <span className="text-sm lg:text-lg font-bold text-primaryBlue">
              {currency === 'NGN' ? `₦${totalCost}` : `$${totalCost}`}
            </span>
          </div>
        )}

        <div className="lg:col-span-12 border-t border-gray-200 w-full flex justify-between items-center md:flex-nowrap p-6">
          <button
            type="button"
            className="px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center"
            onClick={() => navigate('/manageLoadBalancers')}
          >
            Cancel
          </button>

          <div className="w-full flex justify-between md:justify-end items-end md:items-center gap-2">
            <button
              type="button"
              className={`px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center ${
                tabsBalancers.indexOf(selectedTab) === 0 && tabPage === 1
                  ? 'opacity-50'
                  : 'opacity-100'
              }`}
              onClick={navigateToPrevious}
              disabled={
                tabsBalancers.indexOf(selectedTab) === 0 && tabPage === 1
              }
            >
              {'<'} Back
            </button>

            {selectedTab !== tabsBalancers[tabsBalancers.length - 1] ? (

              <button
                type="button"
                className={`px-4 py-4 text-xs border border-primaryBlue rounded-sm text-primaryBlue cursor-pointer text-center ${
                  tabsBalancers.indexOf(selectedTab) ===
                  tabsBalancers.length - 1
                    ? 'opacity-50'
                    : 'opacity-100'
                }`}
                onClick={navigateToNext}
                disabled={
                  tabsBalancers.indexOf(selectedTab) ===
                  tabsBalancers.length - 1
                }
              >
                Next {'>'}
              </button>
            ) : (
             <Button
              buttonText={
                unitPriceLoading
                  ? 'Loading price...'
                  : walletLoading
                    ? 'Checking wallet...'
                    : walletBalance === null
                      ? 'Check Wallet & Continue'
                      : totalCost > walletBalance
                        ? 'Pay Now'
                        : 'Create Load Balancer'
              }
              buttonRole="submit"
              type="submit"
              handleClick={createLoadBalanceForm.handleSubmit}
              disabled={
                !isTabValid['Load Balancer Details *'] ||
                unitPriceLoading ||
                walletLoading ||
                (
                  createLoadBalanceForm.values.createPool === 'yes' &&
                  poolMemberUnitPrice === null
                )
              }
            />
            )}
          </div>
        </div>
      </div>

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        currency={currency}
        totalPrice={totalCost}
        walletBalance={walletBalance}
        virtualAccount={virtualAccount}
        onPaymentSuccess={() => createLoadBalanceForm.handleSubmit()}
      />
    </div>
  );
};

export default CreateLoadBalancer;

